import CryptoJS, { AES, SHA256, enc, lib } from "crypto-js";

export interface IEncryptedData {
  encryptedValue: string;
  iv: string;
}

export const encrypt = (text: string) => {
  return btoa(text);
};

export const decrypt = (text: string) => {
  try {
    return atob(text);
  } catch (err) {
    console.error("Error: ", err);
    return false;
  }
};

const IV_STRING_LENGTH = 16;
export const encryptPayload = (data: object): IEncryptedData => {
  const secret = process.env.REACT_APP_API_DECRYPT_KEY || "";
  const iv = lib.WordArray.random(IV_STRING_LENGTH);
  const key = SHA256(secret);
  const value = enc.Utf8.parse(JSON.stringify(data));
  const encryptedHash = AES.encrypt(value, key, { iv }).toString();
  const encryptedValue = enc.Base64.parse(encryptedHash).toString(enc.Hex);
  return { iv: iv.toString(), encryptedValue };
};

export const decryptPayload = async (payload: IEncryptedData) => {
  const secret = process.env.REACT_APP_API_DECRYPT_KEY;

  if (!payload?.encryptedValue || !payload?.iv) {
    return new Error("Invalid response data");
  }

  if (!secret) {
    return new Error("Failed to decrypt payload");
  }

  const { encryptedValue, iv } = payload;

  // Convert inputs to ArrayBuffer
  const keyMaterial = new TextEncoder().encode(secret);
  const ivBuffer = new Uint8Array(
    iv.match(/[\da-f]{2}/gi)!.map((h) => parseInt(h, 16))
  );
  const encryptedBuffer = new Uint8Array(
    encryptedValue.match(/[\da-f]{2}/gi)!.map((h) => parseInt(h, 16))
  );

  // Derive a 256-bit AES key from the secret
  const key = await crypto.subtle.importKey(
    "raw",
    await crypto.subtle.digest("SHA-256", keyMaterial),
    { name: "AES-CBC" },
    false,
    ["decrypt"]
  );

  try {
    // Decrypt the payload
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: "AES-CBC", iv: ivBuffer },
      key,
      encryptedBuffer
    );

    const decryptedText = new TextDecoder().decode(decryptedBuffer);

    if (!decryptedText) return undefined;

    return JSON.parse(decryptedText);
  } catch (error) {
    console.error(error);
    return new Error("Failed to decrypt payload");
  }
};

const calculateMD5 = (data: any) => {
  return CryptoJS.MD5(data).toString();
};

export const getFileBufferObject = (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      if (!evt.target?.result) return;
      const fileData = new Uint8Array(evt.target.result as ArrayBufferLike);

      // Calculate the MD5 hash of the file data
      const md5 = calculateMD5(fileData);

      // Prepare the file object
      const fileObject = {
        name: file.name,
        data: {
          type: "Buffer",
          data: Array.from(fileData), // Convert Uint8Array to regular array
        },
        size: file.size,
        encoding: "7bit",
        mimetype: file.type,
        md5: md5,
      };

      resolve(fileObject);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsArrayBuffer(file);
  });
};
